import { ThemeMode } from '@rbx/ui';

type IconManifest = Record<string, { Dark?: string; Light?: string }>;

const ICONS_BASE_PATH = `${process.env.staticFilesPrefix}/assets/engine-reference-icons`;
const MANIFEST_PATH = `${ICONS_BASE_PATH}/engine-reference-icons-manifest.json`;

let cachedManifest: IconManifest | null = null;
let manifestPromise: Promise<IconManifest> | null = null;

/**
 * Load the engine reference icons manifest
 * @returns The manifest object or a promise for it
 */
const loadManifest = (): IconManifest | Promise<IconManifest> => {
  // Return cached manifest synchronously
  if (cachedManifest) {
    return cachedManifest;
  }

  // Deduplicate concurrent requests
  if (manifestPromise) {
    return manifestPromise;
  }

  manifestPromise = fetch(MANIFEST_PATH)
    .then((response) => response.json())
    .then((manifest: IconManifest) => {
      cachedManifest = manifest;
      manifestPromise = null;
      return manifest;
    });

  return manifestPromise;
};

/**
 * Get icon path from manifest
 */
const getIconPathFromManifest = (
  manifest: IconManifest,
  className: string,
  themeMode: ThemeMode,
): string | null => {
  const iconEntry = manifest[className];
  if (!iconEntry) {
    return null;
  }

  const themeKey = themeMode.charAt(0).toUpperCase() + themeMode.slice(1);
  const iconFileName = iconEntry[themeKey as 'Dark' | 'Light'];

  if (!iconFileName) {
    return null;
  }

  return `${ICONS_BASE_PATH}/${iconFileName}`;
};

/**
 * Synchronously get the icon path if manifest is already loaded
 * @param className - The class name (e.g. "SurfaceAppearance")
 * @param themeMode - The theme mode ("light" or "dark")
 * @returns The icon path, or null if not found or manifest not loaded
 */
export const getEngineReferenceIconSync = (
  className: string,
  themeMode: ThemeMode,
): string | null => {
  if (!cachedManifest) {
    return null;
  }
  return getIconPathFromManifest(cachedManifest, className, themeMode);
};

/**
 * Get the icon path for a given class name and theme mode
 * @param className - The class name (e.g. "SurfaceAppearance")
 * @param themeMode - The theme mode ("light" or "dark")
 * @returns The icon path, or null if not found
 */
export const getEngineReferenceIcon = async (
  className: string,
  themeMode: ThemeMode,
): Promise<string | null> => {
  const manifest = await loadManifest();
  return getIconPathFromManifest(manifest, className, themeMode);
};
